import { MobileDrawer } from '@components/MobileDrawer/MobileDrawer';
import { Switch } from '@components/Switch/Switch';
import { useLibraryViewMode } from '@components/ViewModeToggle/useViewModePreference';
import { VIEW_MODE_LABELS } from '@components/ViewModeToggle/ViewModeToggle';
import CheckIcon from '@mui/icons-material/Check';
import TuneRoundedIcon from '@mui/icons-material/TuneRounded';
import ViewListIcon from '@mui/icons-material/ViewList';
import ViewModuleIcon from '@mui/icons-material/ViewModule';
import { getSortOptionKey, getVisibleSortGroups } from '@pages/Library/utils/sortOptions';
import { useLibraryFolder } from '@stores/Library/LibraryFolder';
import { trackingStore } from '@stores/Tracking';
import { useUIPreferences } from '@stores/UIPreferences';
import classNames from 'classnames';
import { observer } from 'mobx-react';
import { ReactNode, useId, useState } from 'react';
import css from './MobileViewOptions.module.scss';

const Option = ({
  icon,
  label,
  checked,
  onClick,
}: {
  icon?: ReactNode;
  label: string;
  checked: boolean;
  onClick: () => void;
}) => (
  <button
    type="button"
    className={classNames(css.option, checked && css.selected)}
    aria-pressed={checked}
    onClick={onClick}
  >
    {icon && <span className={css.optionIcon}>{icon}</span>}
    <span className={css.optionLabel}>{label}</span>
    {checked && <CheckIcon className={css.checkIcon} />}
  </button>
);

/**
 * A phone's view, sort and autoplay, behind one button on the search line. The phone had them as
 * three bevelled circles floating over the media, and then as a second row of controls under the
 * buttons; both crowded a screen this narrow.
 *
 * Sort shows in both views: the phone's list has no column headers to sort by. Autoplay shows in
 * the gallery only, since the list plays nothing. Picking a view or a sort closes the sheet, as the
 * single-purpose sheets did; autoplay is a switch and leaves it open.
 */
export const MobileViewOptions = observer(({ buttonClassName }: { buttonClassName: string }) => {
  const [open, setOpen] = useState(false);
  const folder = useLibraryFolder();
  const uiPreferences = useUIPreferences();
  const viewMode = useLibraryViewMode();
  const autoplayId = useId();
  const sortKey = getSortOptionKey(folder.effectiveSortModel);
  const close = () => setOpen(false);

  return (
    <>
      <button
        type="button"
        className={buttonClassName}
        aria-label="View, sort and autoplay"
        aria-haspopup="dialog"
        aria-expanded={open}
        onClick={() => setOpen(true)}
      >
        <TuneRoundedIcon />
      </button>
      <MobileDrawer open={open} onClose={close} onOpen={() => setOpen(true)} frosted>
        <div className={css.content}>
          <span className={css.title}>View</span>
          <div className={css.group}>
            {(['grid', 'list'] as const).map((mode) => (
              <Option
                key={mode}
                icon={mode === 'grid' ? <ViewModuleIcon /> : <ViewListIcon />}
                label={VIEW_MODE_LABELS[mode]}
                checked={viewMode.value === mode}
                onClick={() => {
                  if (viewMode.value !== mode) viewMode.onChange(mode);
                  close();
                }}
              />
            ))}
          </div>
          {viewMode.value === 'grid' && (
            <label className={css.switchRow} htmlFor={autoplayId}>
              <span className={css.switchText}>
                Autoplay
                <span className={css.switchHint}>Play media when it's centred on screen</span>
              </span>
              <Switch
                id={autoplayId}
                checked={uiPreferences.autoplayVideos}
                isLoading={uiPreferences.isLoading}
                onChange={(enabled) => {
                  uiPreferences.setAutoplayVideos(enabled);
                  trackingStore.autoplayToggled({ type: 'videos', enabled });
                }}
              />
            </label>
          )}
          <span className={css.title}>Sort by</span>
          {getVisibleSortGroups(folder.hasPublishedAtMedia).map((group, index) => (
            <div key={index} className={css.group}>
              {group.map((option) => (
                <Option
                  key={option.key}
                  label={option.label}
                  checked={sortKey === option.key}
                  onClick={() => {
                    folder.setSortModel(option.sortModel);
                    trackingStore.librarySortChanged({ sortBy: option.key });
                    close();
                  }}
                />
              ))}
            </div>
          ))}
        </div>
      </MobileDrawer>
    </>
  );
});
